import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

export function useMediaQuery(q: string): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = window.matchMedia(q);
      m.addEventListener("change", l);
      return () => m.removeEventListener("change", l);
    },
    () => window.matchMedia(q).matches,
    () => false,
  );
}

export type Layout = "phone" | "tablet" | "desktop";
/** phone < 768 ≤ tablet < 1200 ≤ desktop — the same breakpoints as shell.css. */
export function useLayout(): Layout {
  const tablet = useMediaQuery("(min-width: 768px)");
  const desktop = useMediaQuery("(min-width: 1200px)");
  return desktop ? "desktop" : tablet ? "tablet" : "phone";
}

export const useReducedMotion = () => useMediaQuery("(prefers-reduced-motion: reduce)");

/** Ticks every `ms` so relative times and countdowns stay fresh. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
}

/** Close on outside pointerdown / Escape. */
export function useDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLElement | null>(null);
  const cb = useRef(onClose);
  cb.current = onClose;
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) cb.current();
    };
    const key = (e: KeyboardEvent) => e.key === "Escape" && cb.current();
    document.addEventListener("pointerdown", down);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("pointerdown", down);
      document.removeEventListener("keydown", key);
    };
  }, [open]);
  return ref;
}

export function useToggleSet<T>(initial: Iterable<T> = []) {
  const [set, setSet] = useState(() => new Set(initial));
  const toggle = useCallback((v: T) => setSet((s) => {
    const n = new Set(s);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    return n;
  }), []);
  return [set, toggle, setSet] as const;
}
